import React, { useEffect, useState, createContext, useContext } from "react";
import { Link } from "@/lib/nav";
import { Lock, ArrowRight, LogOut } from "lucide-react";
import { licenseStatusReal, isOwner } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const LicenseContext = createContext(null);
export const useLicense = () => useContext(LicenseContext);

// Real backend licensing is platform-level (edition). On top of it we enforce the
// per-tenant trial / paid-license expiry: when expired (and not renewed) modules are
// locked. The owner account is never blocked. The /license page stays reachable.
export default function LicenseGate({ children }) {
  const { user, logout } = useAuth();
  const [st, setSt] = useState(null);
  const load = () => licenseStatusReal().then(setSt).catch(() => setSt(null));
  useEffect(() => { load(); }, []);

  const lic = user?.license;
  const trial = user?.trial;
  const onprem = !!lic && lic.onprem === true;
  const licenseExpired = !!lic && lic.expired === true && !onprem;
  const trialExpired = !lic && !!trial?.is_trial && trial?.days_left === 0;
  const blocked = !isOwner(user) && (onprem || licenseExpired || trialExpired);

  if (blocked) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center px-4" data-testid="license-expired-screen">
        <div className="w-full max-w-lg border border-[#ff3333]/40 bg-[#0a0a0a] p-8 text-center">
          <Lock className="mx-auto h-10 w-10 text-[#ff5a5a]" />
          <h1 className="mt-4 font-display text-2xl font-700">
            {onprem ? "La tua istanza è sul tuo server" : licenseExpired ? "Licenza scaduta" : "Licenza non attiva"}
          </h1>
          <p className="mt-3 text-sm text-[#a3a3a3]">
            {onprem
              ? "La tua licenza Lattice è attiva sul TUO server aziendale (on-premise). Il sito ufficiale non è il tuo ambiente di lavoro: accedi all'istanza installata sul tuo dominio. Hai ricevuto le istruzioni via Certify."
              : licenseExpired
              ? `La licenza ${lic.package_label} è scaduta e non è stata rinnovata. L'accesso ai moduli è disattivato finché non viene riattivata una nuova licenza.`
              : "I 3 giorni di prova sono terminati. Attiva una licenza per continuare a usare Lattice."}
          </p>
          <div className="mt-6 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            {!onprem && (
              <Link to="/license" data-testid="expired-activate-cta"
                className="inline-flex items-center gap-2 border lp-bd lp-b px-5 py-2.5 font-mono text-sm uppercase tracking-wider text-white hover:bg-[#3fa862]">
                {licenseExpired ? "Rinnova licenza" : "Attiva licenza"} <ArrowRight className="h-4 w-4" />
              </Link>
            )}
            <button onClick={logout} data-testid="expired-logout"
              className="inline-flex items-center gap-2 border border-[#262626] px-5 py-2.5 font-mono text-sm uppercase tracking-wider text-[#a3a3a3] hover:text-white">
              <LogOut className="h-4 w-4" /> Esci
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <LicenseContext.Provider value={{ st, reload: load }}>
      {children}
    </LicenseContext.Provider>
  );
}

// All modules are governed by server-side RBAC, not per-tenant module purchase.
export const ModuleGuard = ({ children }) => children;
